// components/RegisterPage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { prepareRegistrationData } from '../utils/crypto';
import { Eye, EyeOff, Lock, Mail, AlertCircle } from 'lucide-react';
import RecoveryKeyDisplay from './RecoveryKeyDisplay';

const RegisterPage = ({ 
  projectId, 
  projectName = 'App',
  loginPath = '/login',
  apiUrl = 'http://localhost:8000',
  brandColor = 'slate'
}) => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [recoveryKey, setRecoveryKey] = useState('');
  const [showRecoveryKey, setShowRecoveryKey] = useState(false);

  // Color scheme based on brandColor prop
  const colorSchemes = {
    slate: {
      gradient: 'from-slate-900 via-slate-800 to-slate-900',
      button: 'from-slate-800 to-slate-900 hover:from-slate-700 hover:to-slate-800',
      ring: 'focus:ring-slate-500',
      text: 'text-slate-700 hover:text-slate-900'
    },
    blue: {
      gradient: 'from-blue-900 via-blue-800 to-blue-900',
      button: 'from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600',
      ring: 'focus:ring-blue-500',
      text: 'text-blue-600 hover:text-blue-800'
    },
    green: {
      gradient: 'from-green-900 via-green-800 to-green-900',
      button: 'from-green-600 to-green-700 hover:from-green-500 hover:to-green-600',
      ring: 'focus:ring-green-500',
      text: 'text-green-600 hover:text-green-800'
    },
    purple: {
      gradient: 'from-purple-900 via-purple-800 to-purple-900',
      button: 'from-purple-600 to-purple-700 hover:from-purple-500 hover:to-purple-600',
      ring: 'focus:ring-purple-500',
      text: 'text-purple-600 hover:text-purple-800'
    }
  };

  const colors = colorSchemes[brandColor] || colorSchemes.slate;

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    setError('');
  };

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return null;
  };

  const validatePassword = (password) => {
    if (password.length < 8) return 'Password must be at least 8 characters';
    if (!/[A-Z]/.test(password)) return 'Password must contain at least one uppercase letter';
    if (!/[a-z]/.test(password)) return 'Password must contain at least one lowercase letter';
    if (!/[0-9]/.test(password)) return 'Password must contain at least one number';
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Email validation
    const emailError = validateEmail(formData.email);
    if (emailError) {
      setError(emailError);
      setLoading(false);
      return;
    }

    // Password validation
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    const passwordError = validatePassword(formData.password);
    if (passwordError) {
      setError(passwordError);
      setLoading(false);
      return;
    }

    try {
      // Prepare encrypted data on client side
      const registrationData = await prepareRegistrationData(
        formData.email,
        formData.password,
        projectId
      );

      // Store recovery key to show user
      setRecoveryKey(registrationData.recoveryKey);

      // Remove recovery key from data sent to server (only send hash)
      const { recoveryKey: _, ...dataToSend } = registrationData;

      // Send to backend
      const response = await fetch(`${apiUrl}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataToSend),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle specific error cases
        if (response.status === 409 || errorData.detail?.includes('already registered')) {
          throw new Error('An account with this email already exists. Please sign in instead.');
        } else if (response.status === 404 && errorData.detail?.includes('Project')) {
          throw new Error('Invalid project configuration. Please contact support.');
        } else if (response.status === 400) {
          throw new Error(errorData.detail || 'Invalid registration data');
        }
        
        throw new Error(errorData.detail || 'Registration failed');
      }

      const result = await response.json();
      
      // Store tokens in sessionStorage (more secure than localStorage)
      sessionStorage.setItem('access_token', result.access_token);
      sessionStorage.setItem('refresh_token', result.refresh_token);
      sessionStorage.setItem('user', JSON.stringify({
        id: result.user.id,
        email: result.user.email,
        public_key: result.user.public_key
      }));

      // Show recovery key screen
      setShowRecoveryKey(true);
      setLoading(false);

    } catch (err) {
      console.error('Registration error:', err);
      
      // Handle network errors
      if (err.name === 'TypeError' && err.message === 'Failed to fetch') {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError(err.message || 'Registration failed. Please try again.');
      }
      
      setLoading(false);
    }
  };

  const handleRecoveryKeySaved = () => {
    navigate(loginPath);
  };

  // Recovery Key Screen
  if (showRecoveryKey) {
    return (
      <RecoveryKeyDisplay
        recoveryKey={recoveryKey}
        projectName={projectName}
        userEmail={formData.email}
        onConfirm={handleRecoveryKeySaved}
        confirmButtonText="I have saved my recovery key - Continue to Login"
        brandColor={brandColor}
        showWarning={true}
        requireConfirmation={true}
      />
    );
  }

  // Registration Form Screen
  return (
    <div className={`min-h-screen bg-gradient-to-br ${colors.gradient} flex items-center justify-center p-4`}>
      <div className="max-w-md w-full">
        <div className="text-center mb-8">
          <div className="inline-block bg-white p-4 rounded-2xl shadow-lg mb-4">
            <div className="text-4xl font-bold text-gray-900">{projectName}</div>
          </div>
          <h2 className="text-3xl font-bold text-white mb-2">Create Account</h2>
          <p className="text-gray-300">Join {projectName} with end-to-end encryption</p>
        </div>

        <div className="bg-white rounded-2xl shadow-2xl p-8">
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <Mail className="w-4 h-4" />
                Email Address
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                required
                disabled={loading}
                className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${colors.ring} focus:border-transparent transition-all disabled:bg-gray-50 disabled:cursor-not-allowed`}
                placeholder="you@example.com"
                autoComplete="email"
              />
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <Lock className="w-4 h-4" />
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  minLength={8}
                  className={`w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${colors.ring} focus:border-transparent transition-all disabled:bg-gray-50 disabled:cursor-not-allowed`}
                  placeholder="Create a strong password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  disabled={loading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Must be 8+ characters with uppercase, lowercase, and number
              </p>
            </div>

            <div>
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-2">
                <Lock className="w-4 h-4" />
                Confirm Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  disabled={loading}
                  className={`w-full px-4 py-3 pr-12 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 ${colors.ring} focus:border-transparent transition-all disabled:bg-gray-50 disabled:cursor-not-allowed`}
                  placeholder="Confirm your password"
                  autoComplete="new-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  disabled={loading}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 disabled:opacity-50"
                >
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {error && (
              <div className="bg-red-50 border-2 border-red-200 text-red-700 px-4 py-3 rounded-xl flex items-start gap-3">
                <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className={`w-full bg-gradient-to-r ${colors.button} text-white py-4 px-6 rounded-xl font-semibold shadow-lg transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                  </svg>
                  Creating Account...
                </span>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-gray-600">
              Already have an account?{' '}
              <button
                onClick={() => navigate(loginPath)}
                disabled={loading}
                className={`${colors.text} font-semibold transition-colors disabled:opacity-50`}
              >
                Sign In
              </button>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-gray-400">
            Secured with end-to-end encryption
          </p>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;